import axios, { CancelToken } from "axios";
import request from "../../utils/request";
import { apiErrorsOf, httpStatusOf } from "../../utils/requestHandling";
import { GoalDashboardStatsView, GoalProgressIndicator } from "@/models/preformanceManagement";
// Helper function to extract payload from Result wrapper
function extractPayload<T>(data: any): T {
  // If data is already the payload (not wrapped in Result), return it
  if (Array.isArray(data)) {
    return data as T;
  }

  // If data has a payload property, extract it
  if (data && typeof data === 'object' && 'payload' in data) {
    return data.payload as T;
  }

  // Otherwise return as-is
  return data as T;
}

// This axios (0.21) predates AbortSignal support, so a caller's signal is bridged onto a CancelToken.
const cancelTokenFor = (signal?: AbortSignal): CancelToken | undefined => {
  if (!signal) return undefined;
  const source = axios.CancelToken.source();
  if (signal.aborted) {
    source.cancel('canceled');
  } else {
    signal.addEventListener('abort', () => source.cancel('canceled'), { once: true });
  }
  return source.token;
};

// True for a request the caller cancelled; those must be re-thrown, never turned into a toast.
export const isAbortError = (err: unknown): boolean => {
  if (axios.isCancel(err)) return true;
  const candidate = err as { code?: string; name?: string } | null | undefined;
  if (!candidate) return false;
  return candidate.code === 'ERR_CANCELED' || candidate.name === 'CanceledError' || candidate.name === 'AbortError';
};

/**
 * CO-574: the axios interceptor (utils/requestHandling.ts) rejects with the PARSED `Result` body — a plain
 * object with no `.response` — so every `error.response?.data?.errors` read in this module was `undefined`
 * and the server's refusal text died here, one layer below ~11 components that correctly render a thrown
 * `Error`'s message. Read the refusal through `apiErrorsOf`, which walks every wrapper shape and the
 * non-enumerable `__apiErrors` stamp; then fall back to a foreign `.message` (JS/axios Error), then the
 * caller's fallback. Never throws.
 */
function failureMessage(error: unknown, fallback: string): string {
  const descriptions = apiErrorsOf(error)
    .map((e) => (typeof e.description === 'string' && e.description.trim() ? e.description : (e as { message?: unknown }).message))
    .filter((d): d is string => typeof d === 'string' && d.trim().length > 0);
  if (descriptions.length > 0) return descriptions.join('\n');
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

/** True when the rejection carries at least one structured server error. */
export const hasServerErrors = (error: unknown): boolean => apiErrorsOf(error).length > 0;

/**
 * CO-577 — rethrow a server refusal as an Error WITHOUT losing the transport evidence.
 *
 * `throw new Error(failureMessage(...))` produced a clean message and threw away the interceptor's
 * `__httpStatus` and `__apiErrors` stamps, so `useGoals` could never branch on a status. Both are
 * re-attached non-enumerably, exactly as the interceptor attaches them, so spreads and JSON
 * round-trips are unaffected.
 */
export function rethrowServerError(error: unknown, fallback: string): never {
  const err = new Error(failureMessage(error, fallback));
  const status = httpStatusOf(error);
  if (status !== undefined) {
    Object.defineProperty(err, '__httpStatus', {
      value: status, enumerable: false, configurable: true, writable: true,
    });
  }
  const errors = apiErrorsOf(error);
  if (errors.length > 0) {
    Object.defineProperty(err, '__apiErrors', {
      value: errors, enumerable: false, configurable: true, writable: true,
    });
  }
  throw err;
}

// API 1: Get all goals with optional filtering
export async function getAllGoals(filter?: GoalFilterOptions, signal?: AbortSignal): Promise<GoalView[]> {
  try {
    const response = await request.get(`Goals`, {
      params: filter,
      cancelToken: cancelTokenFor(signal)
    });

    return extractPayload<GoalView[]>(response.data);
  } catch (error: any) {
    if (isAbortError(error)) throw error;

    rethrowServerError(error, 'Failed to fetch goals');
  }
}

// API 2: Get goal by ID
export async function getGoalById(goalId: string): Promise<GoalView> {
  try {
    const response = await request.get(`Goals/${goalId}`);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// API 3: Create new goal
export async function createGoal(input: GoalInput): Promise<GoalView> {
  try {
    const response = await request.post(`Goals`, input);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// API 4: Update existing goal
export async function updateGoal(goalId: string, input: GoalInput): Promise<GoalView> {
  try {
    const response = await request.put(`Goals/${goalId}`, input);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// API 5: Delete goal
export async function deleteGoal(goalId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/${goalId}`);

    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// API 6: Update goal progress
export async function updateGoalProgress(input: GoalProgressUpdateInput): Promise<GoalProgressUpdateView> {
  try {
    const response = await request.put(`Goals/${input.goalId}/progress`, input);

    return extractPayload<GoalProgressUpdateView>(response.data);
  } catch (error: any) {
    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// API 8: Get goal statistics
export async function getGoalStats(filter?: GoalFilterOptions, signal?: AbortSignal): Promise<GoalStatsView> {
  try {
    const response = await request.get(`Goals/stats`, {
      params: filter,
      cancelToken: cancelTokenFor(signal)
    });

    return extractPayload<GoalStatsView>(response.data);
  } catch (error: any) {
    if (isAbortError(error)) throw error;

    if (hasServerErrors(error)) {
      rethrowServerError(error, 'Request failed');
    }

    throw error;
  }
}

// Get user's company departments
export async function getUserCompanyDepartments(): Promise<DepartmentBasicView[]> {
  try {
    const response = await request.get(`Goals/departments`);

    return extractPayload<DepartmentBasicView[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch departments');
  }
}

// Get available owners for goals (role-based filtering)
export async function getAvailableOwners(): Promise<GoalOwnerView[]> {
  try {
    const response = await request.get(`Goals/available-owners`);

    return extractPayload<GoalOwnerView[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch available owners');
  }
}

export async function getGoalDashboardStats(): Promise<GoalDashboardStatsView> {
  try {
    const response = await request.get(`Goals/dashboard/stats`);

    return extractPayload<GoalDashboardStatsView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch goal dashboard statistics');
  }
}

// API: Get goal progress indicators
export async function getGoalProgressIndicators(): Promise<GoalProgressIndicator[]> {
  try {
    const response = await request.get(`Goals/dashboard/progress-indicators`);

    return extractPayload<GoalProgressIndicator[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch goal progress indicators');
  }
}

// Achievement APIs
export async function hasGoalGetterAchievement(): Promise<boolean> {
  try {
    const response = await request.get(`Goals/achievements/goal-getter`);

    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to check Goal Getter achievement');
  }
}

export async function hasConsistentAchieverAchievement(): Promise<boolean> {
  try {
    const response = await request.get(`Goals/achievements/consistent-achiever`);

    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to check Consistent Achiever achievement');
  }
}

export async function getGoalAchievementProgress(): Promise<GoalAchievementProgressView> {
  try {
    const response = await request.get(`Goals/achievements/progress`);

    return extractPayload<GoalAchievementProgressView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to get goal achievement progress');
  }
}
// API: Get goal alignment tree (hierarchical)
export async function getGoalTree(rootGoalId?: string, signal?: AbortSignal): Promise<GoalTreeView> {
  try {
    const params: Record<string, string> = {};
    if (rootGoalId) params.rootGoalId = rootGoalId;
    const response = await request.get(`Goals/tree`, { params, cancelToken: cancelTokenFor(signal) });
    return extractPayload<GoalTreeView>(response.data);
  } catch (error: any) {
    if (isAbortError(error)) throw error;

    rethrowServerError(error, 'Failed to fetch goal tree');
  }
}

// Goal AI APIs
export async function suggestGoal(input: GoalSuggestInput): Promise<GoalSuggestion> {
  const response = await request.post(`GoalAi/SuggestGoal`, input);
  return extractPayload<GoalSuggestion>(response.data);
}

export async function analyzeGoalClarity(input: GoalClarityAnalyzeInput): Promise<GoalClarityAnalysis> {
  const response = await request.post(`GoalAi/AnalyzeClarity`, input);
  return extractPayload<GoalClarityAnalysis>(response.data);
}

// Types
export interface GoalTreeView {
  goal?: GoalView;
  children?: GoalTreeView[];
}

export enum EquipmentPriority {
  Essential = "Essential", Important = "Important", Helpful = "Helpful", Optional = "Optional",
}

export interface NeededEquipmentItem {
  categoryId: number;
  categoryName?: string;
  priority: EquipmentPriority;
  quantity: number;
}

export interface GoalClarityField {
  field: string;
  percent: number;
  note?: string;
}

/**
 * How a stored clarity score was actually derived (CO-195).
 *
 * Serialized as a STRING: the API registers Newtonsoft's `StringEnumConverter`
 * (`Program.cs:831`), so the C# enum arrives as its name, not its ordinal.
 * `Unknown` is what a record scored before provenance existed reads as, which is
 * why the enum's zero value is Unknown and not `Ai`.
 */
export type ClarityScoreSource = 'Unknown' | 'Ai' | 'Fallback' | 'Capped' | 'Quarantined';

export interface GoalClarityAnalysis {
  overallPercent: number;
  perField: GoalClarityField[];
  recommendations: string[];
  isUnclear: boolean;
  aiAvailable: boolean;
  scoreSource?: ClarityScoreSource;
}

export interface GoalSuggestion {
  description?: string;
  metricType?: number | null;
  target?: string;
  milestones: string[];
  skills: string[];
  neededEquipment: NeededEquipmentItem[];
  rationale?: string;
  aiAvailable: boolean;
}

export interface GoalSuggestInput {
  title: string;
  level?: number;
  ownerId?: string;
  parentGoalId?: string;
  departmentId?: number;
  dueDate?: string;
}

export interface GoalClarityAnalyzeInput {
  // Set for a saved goal so an unchanged score is reused instead of re-scored.
  goalId?: string;
  title: string;
  description?: string;
  level?: number;
  metricType?: number | null;
  target?: string;
  milestones?: { title?: string; dueDate?: string }[];
  skills?: string[];
  neededEquipment?: NeededEquipmentItem[];
  parentGoalId?: string;
  startDate?: string;
  dueDate?: string;
}

export interface GoalFilterOptions {
  department?: string;
  status?: string;
  level?: string;
  priority?: string;
  tags?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  onlyUnclear?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface GoalInput {
  title: string;
  description: string;
  level: number;
  status: number;
  progress: number;
  ownerId: string;
  departmentId?: number | null;
  categoryId?: string | null;
  parentId?: string | null;
  startDate: string;
  dueDate: string;
  priority: number;
  metricType?: number | null;
  target?: string | null;
  skillIds?: string | null;
  milestones?: GoalMilestoneInput[];
  assignments?: GoalAssignmentInput[];
  tags?: string[];
  neededEquipment?: NeededEquipmentItem[];
}

export interface GoalMilestoneInput {
  title: string;
  description: string;
  isCompleted: boolean;
  dueDate: string;
  order: number;
}

export interface GoalAssignmentInput {
  userId: string;
  assignmentType: number;
}

export interface GoalView {
  id: string;
  title: string;
  description: string;
  level: string | number;
  status: number;
  progress: number;
  userId: string;
  departmentId?: number;
  categoryId?: string;
  parentId?: string;
  startDate: string;
  dueDate: string;
  completedDate?: string;
  priority: number;
  metricType?: number | null;
  metricTypeName?: string | null;
  target?: string | null;
  skillIds?: string | null;
  // Server-resolved names for skillIds (GoalService.ResolveSkillNames). Without these the client can
  // only render raw ids, which is exactly what the goal modal used to do.
  skills?: { id: number; name: string; description?: string }[];
  isDeleted: boolean;
  createdAt: string;
  updatedAt?: string;
  owner?: {
    id: string;
    name?: string;
    email?: string;
  };
  department?: {
    id: number;
    name: string;
  };
  category?: {
    id: string;
    name: string;
  };
  milestones?: any[];
  comments?: any[];
  assignments?: any[];
  tags?: string[];
  hasActiveBlockers?: boolean;
  activeBlockerCount?: number;
  neededEquipment?: NeededEquipmentItem[];
  clarityScore?: number | null;
  clarity?: GoalClarityAnalysis | null;
  clarityScoreUpdatedOn?: string | null;
}

// The list projection (server: GoalListItemView). Field names are picked from GoalView so the two
// stay aligned; the heavy child collections are dropped and milestone counts replace them.
export interface GoalListItemView extends Pick<GoalView,
  'id' | 'title' | 'description' | 'level' | 'progress' | 'priority' | 'parentId' | 'owner' |
  'departmentId' | 'department' | 'startDate' | 'dueDate' | 'clarityScore' | 'clarityScoreUpdatedOn' |
  'skillIds' | 'hasActiveBlockers' | 'activeBlockerCount' | 'isDeleted' | 'createdAt' | 'updatedAt'> {
  // The paged projection serializes status as the enum name, where GoalView carries the number.
  status: string | number;
  ownerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  departmentName?: string;
  milestoneTotalCount: number;
  milestoneCompletedCount: number;
}

export interface GoalProgressUpdateInput {
  goalId: string;
  progress: number;
  status?: number;
  notes?: string;
  updatedAt?: string;
}

export interface GoalProgressUpdateView {
  id: number;
  goalId: string;
  userId: string;
  previousProgress: number;
  newProgress: number;
  previousStatus: number;
  newStatus: number;
  note?: string;
  createdAt: string;
}

export interface GoalStatsView {
  totalGoals: number;
  completedGoals: number;
  onTrackGoals: number;
  atRiskGoals: number;
  overdueGoals: number;
  totalMilestones: number;
  completedMilestones: number;
  goalsByLevel: { [key: string]: number };
  goalsByStatus: { [key: string]: number };
  averageProgress?: number;
}

export interface DepartmentBasicView {
  id: number;
  name: string;
}

export interface GoalOwnerView {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  departmentId?: number;
  departmentName?: string;
  profilePictureUrl?: string;
}

export interface GoalAchievementProgressView {
  goalGetterProgress: number;
  goalGetterTarget: number;
  goalGetterUnlocked: boolean;
  consistentAchieverProgress: number;
  consistentAchieverTarget: number;
  consistentAchieverUnlocked: boolean;
}

// Audit Log Types
export interface FieldChangeView {
  field: string;
  oldValue?: string;
  newValue?: string;
}

export interface GoalAuditLogView {
  id: string;
  goalId: string;
  changedById: string;
  changedByName?: string;
  changedAt: string;
  changes: FieldChangeView[];
  comment?: string;
}

// Blocker Types
export interface GoalBlockerView {
  id: string;
  goalId: string;
  description: string;
  severity: string;
  requiredResources?: string;
  status: string;
  resolutionNote?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  createdByName?: string;
  createdAt: string;
}

export interface GoalBlockerInput {
  description: string;
  severity: number;
  requiredResources?: string;
}

export interface GoalBlockerResolveInput {
  resolutionNote: string;
}

// Audit Log API
export async function getGoalAuditLog(goalId: string): Promise<GoalAuditLogView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/audit-log`);
    return extractPayload<GoalAuditLogView[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch audit log');
  }
}

// Blocker APIs
export async function getGoalBlockers(goalId: string): Promise<GoalBlockerView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/blockers`);
    return extractPayload<GoalBlockerView[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch blockers');
  }
}

export async function createGoalBlocker(goalId: string, input: GoalBlockerInput): Promise<GoalBlockerView> {
  try {
    const response = await request.post(`Goals/${goalId}/blockers`, input);
    return extractPayload<GoalBlockerView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to create blocker');
  }
}

export async function resolveGoalBlocker(blockerId: string, input: GoalBlockerResolveInput): Promise<GoalBlockerView> {
  try {
    const response = await request.put(`Goals/blockers/${blockerId}/resolve`, input);
    return extractPayload<GoalBlockerView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to resolve blocker');
  }
}

export async function deleteGoalBlocker(blockerId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/blockers/${blockerId}`);
    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    rethrowServerError(error, 'Failed to delete blocker');
  }
}

// Milestone Types
export interface GoalMilestoneView {
  id: string;
  title: string;
  description?: string;
  isCompleted: boolean;
  status: 'Pending' | 'InProgress' | 'Completed' | 'Overdue';
  dueDate?: string;
  completedDate?: string;
  order: number;
}

export interface GoalMilestoneInput {
  title: string;
  description?: string;
  isCompleted: boolean;
  status?: number;
  dueDate?: string;
  order: number;
}

// Outcome Metrics Types
export interface GoalOutcomeMetricsView {
  goalId: string;
  totalMilestones: number;
  completedMilestones: number;
  inProgressMilestones: number;
  overdueMilestones: number;
  milestoneProgressPercent: number;
  expectedReviews: number;
  actualReviews: number;
  cadenceAdherencePercent: number;
  projectedCompletionDate?: string;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  velocityPercent: number;
  projectionStatus: 'OnTrack' | 'AtRisk' | 'Behind' | 'Completed';
}

// Milestone APIs
export async function getGoalMilestones(goalId: string): Promise<GoalMilestoneView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/milestones`);
    return extractPayload<GoalMilestoneView[]>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch milestones');
  }
}

export async function addGoalMilestone(goalId: string, input: GoalMilestoneInput): Promise<GoalMilestoneView> {
  try {
    const response = await request.post(`Goals/${goalId}/milestones`, input);
    return extractPayload<GoalMilestoneView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to add milestone');
  }
}

export async function updateGoalMilestone(milestoneId: string, input: GoalMilestoneInput): Promise<GoalMilestoneView> {
  try {
    const response = await request.put(`Goals/milestones/${milestoneId}`, input);
    return extractPayload<GoalMilestoneView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to update milestone');
  }
}

export async function deleteGoalMilestone(milestoneId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/milestones/${milestoneId}`);
    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    rethrowServerError(error, 'Failed to delete milestone');
  }
}

export async function toggleGoalMilestone(milestoneId: string): Promise<boolean> {
  try {
    const response = await request.put(`Goals/milestones/${milestoneId}/toggle`);
    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to toggle milestone');
  }
}

// Outcome Metrics API
export async function getGoalOutcomeMetrics(goalId: string): Promise<GoalOutcomeMetricsView> {
  try {
    const response = await request.get(`Goals/${goalId}/outcome-metrics`);
    return extractPayload<GoalOutcomeMetricsView>(response.data);
  } catch (error: any) {
    rethrowServerError(error, 'Failed to fetch outcome metrics');
  }
}
